import { IcoCheckCircle16, IcoInfo16, IcoWarning16 } from '@onefootprint/icons';
import { z } from 'zod';

export const toneSchema = z.enum(['positive', 'warning', 'critical', 'neutral']);

export type ArtifactTone = z.infer<typeof toneSchema>;

// `icon` is the design-token color name passed to `@onefootprint/icons` components,
// which take a token name rather than a Tailwind class like the other fields.
type IconColor = 'success' | 'warning' | 'error' | 'neutral';

type BadgeVariant = 'success' | 'warning' | 'error' | 'neutral';

type ToneStyle = {
  badgeVariant: BadgeVariant;
  surface: string;
  value: string;
  marker: string;
  icon: IconColor;
};

// `positive.value` is neutral on purpose: colour marks the exception, not the confirmation.
export const toneStyles: Record<ArtifactTone, ToneStyle> = {
  positive: {
    badgeVariant: 'success',
    surface: 'bg-success text-success border-success',
    value: 'text-primary',
    marker: 'bg-success-inverted',
    icon: 'success',
  },
  warning: {
    badgeVariant: 'warning',
    surface: 'bg-warning text-warning border-warning',
    value: 'text-warning',
    marker: 'bg-warning-inverted',
    icon: 'warning',
  },
  critical: {
    badgeVariant: 'error',
    surface: 'bg-error text-error border-error',
    value: 'text-error',
    marker: 'bg-error-inverted',
    icon: 'error',
  },
  neutral: {
    badgeVariant: 'neutral',
    surface: 'bg-neutral text-neutral border-primary',
    value: 'text-primary',
    marker: 'bg-neutral-inverted',
    icon: 'neutral',
  },
};

// Warning and critical share the triangle, as the app's InlineAlert does; colour carries the rest.
export const iconByTone = {
  positive: IcoCheckCircle16,
  neutral: IcoInfo16,
  warning: IcoWarning16,
  critical: IcoWarning16,
} satisfies Record<ArtifactTone, typeof IcoInfo16>;

export const severityLabel: Record<ArtifactTone, string> = {
  positive: 'Positive',
  neutral: 'Note',
  warning: 'Warning',
  critical: 'Critical',
};
